import { FiActivity, FiClock, FiDollarSign, FiLock, FiPercent, FiTrendingUp } from 'react-icons/fi';
import { Button } from '../components/Button';
import { TraderLeaderboardTable } from '../components/TraderLeaderboardTable';
import { useI18n } from '../i18n/useI18n';

export function LeaderboardPage({
  onViewTrader,
  pushToast,
  isAuthenticated,
  onRequireLogin,
  onRequireMembership,
  onRefreshMembership,
  membershipTier,
  membershipLoading
}) {
  const { t } = useI18n();
  const tierKey = (membershipTier || 'free').toLowerCase();
  const canViewLeaderboard = tierKey !== 'free';

  if (membershipLoading) {
    return (
      <section className="glass-lg w-full overflow-hidden">
        <div className="p-6 md:p-10 animate-pulse space-y-6">
          <div className="h-6 w-40 bg-slate-800/60 rounded-lg" />
          <div className="h-10 w-3/4 bg-slate-800/50 rounded-2xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-28 bg-slate-800/40 rounded-2xl" />
            <div className="h-28 bg-slate-800/40 rounded-2xl" />
          </div>
          <div className="h-64 bg-slate-800/30 rounded-3xl" />
        </div>
      </section>
    );
  }

  if (!canViewLeaderboard) {
    const featureCards = [
      { icon: FiTrendingUp, title: t('leaderboard.boards.rov'), desc: t('leaderboard.boardDesc.rov') },
      { icon: FiDollarSign, title: t('leaderboard.boards.pnl'), desc: t('leaderboard.boardDesc.pnl') },
      { icon: FiPercent, title: t('leaderboard.boards.winRate'), desc: t('leaderboard.boardDesc.winRate') },
      { icon: FiActivity, title: t('leaderboard.boards.trades'), desc: t('leaderboard.boardDesc.trades') },
      { icon: FiClock, title: t('leaderboard.boards.active'), desc: t('leaderboard.boardDesc.active') }
    ];

    const handleRefresh = () => {
      if (onRefreshMembership) {
        onRefreshMembership();
        return;
      }
      window.location.reload();
    };

    return (
      <section className="glass-lg w-full overflow-hidden min-h-[60vh] flex items-center justify-center">
        <div className="relative w-full max-w-5xl px-6 py-10 md:px-10 md:py-12">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-sky-500/10 blur-[120px]" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-violet-500/10 blur-[120px]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-900/60 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-slate-400">
                <FiLock className="text-slate-300" />
                {t('leaderboard.kicker')}
              </div>
              <h1 className="mt-4 text-2xl md:text-3xl font-semibold text-white">
                {t('leaderboard.lockedTitle')}
              </h1>
              <p className="mt-3 text-sm md:text-base text-slate-400 leading-relaxed">
                {t('leaderboard.lockedDesc')}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {isAuthenticated ? (
                  <Button variant="primary" onClick={() => onRequireMembership?.()}>
                    {t('membership.viewPlans')}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={onRequireLogin}>
                    {t('common.login')}
                  </Button>
                )}
                {isAuthenticated && (
                  <Button variant="secondary" onClick={handleRefresh}>
                    {t('common.retry')}
                  </Button>
                )}
              </div>
              <p className="mt-4 text-xs text-slate-500">
                {t('leaderboard.lockedHint')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {featureCards.map((item) => (
                <div
                  key={item.title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-900/70 via-slate-900/40 to-slate-950/70 p-4 shadow-[0_10px_40px_rgba(2,6,23,0.35)]"
                >
                  <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-sky-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative z-10 flex items-start gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-800/70 border border-slate-700/60 text-sky-300">
                      <item.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="glass-lg w-full overflow-hidden">
      <div className="p-4 md:p-6 lg:p-8">
        <TraderLeaderboardTable
          onViewTrader={onViewTrader}
          pushToast={pushToast}
          isAuthenticated={isAuthenticated}
          onRequireLogin={onRequireLogin}
        />
      </div>
    </section>
  );
}
